import { useState, useEffect } from "react";
import { Chart } from "react-chartjs-2";
import {
  Chart as ChartJS, CategoryScale, LinearScale,
  PointElement, LineElement, BarElement,
  Tooltip, Legend, Filler,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { getSprintHistorico } from "../api/sprint.api";
import { getParam } from "../utils/parametros";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Tooltip, Legend, Filler, ChartDataLabels);

function sprintNum(label) {
  const nums = String(label).match(/\d+/g);
  if (!nums) return 0;
  return Math.max(...nums.map(Number));
}

function sprintLabel(raw) {
  const str = String(raw);
  if (/^\d{8}$/.test(str)) return `${str.slice(4, 6)}/${str.slice(6, 8)}`;
  if (/^\d+$/.test(str)) return str;
  const nums = str.match(/\d{3,}/g);
  return nums ? nums[nums.length - 1] : str;
}

function normalizar(raw) {
  if (!Array.isArray(raw) || raw.length === 0) return [];
  return raw.map(item => {
    const saida = item.saida?.geral || item.indicadores?.saida?.geral
               || item.saida        || item.indicadores?.saida
               || item;

    const total   = saida.total     ?? saida.total_geral  ?? 0;
    const pct     = saida.percentual ?? 0;

    let fechado = saida.fechado ?? saida.fechados ?? saida.closed ?? -1;
    if (fechado < 0) {
      fechado = pct > 0 && total > 0 ? Math.round(pct * total / 100) : 0;
    }

    const aberto = saida.aberto  ?? saida.abertos ?? saida.open
                ?? (total - fechado > 0 ? total - fechado : 0);

    const pctFinal = pct > 0 ? pct
                   : (total > 0 ? Math.round(fechado / total * 100) : 0);

    const rawLabel = String(item.sprint || item.versao || item.grupo_entrega || "?");
    const sprintId = item.sprint ? Number(item.sprint) : sprintNum(rawLabel);
    return {
      label:   sprintLabel(rawLabel),
      sortKey: sprintId,
      total, fechado, aberto, pct: pctFinal,
    };
  }).sort((a, b) => a.sortKey - b.sortKey);
}


const devColor = p => p >= 70 ? "#4ade80" : p >= 40 ? "#fbbf24" : "#f87171";

export default function SprintHistorico() {
  const [dados,   setDados]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro,    setErro]    = useState(null);
  const [filtro,  setFiltro]  = useState("todas"); // "todas" | "programadas"

  useEffect(() => {
    setLoading(true);
    setErro(null);
    const tipo = filtro === "programadas" ? "programado" : undefined;
    getSprintHistorico(tipo)
      .then(data => {
        const qtd = getParam("sprints_historico") || 4;
        setDados(normalizar(data).slice(-qtd));
      })
      .catch(() => setErro("Não foi possível carregar o histórico."))
      .finally(() => setLoading(false));
  }, [filtro]);

  if (loading) return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"50vh" }}>
      <span style={{ color:"#475569" }}>Carregando histórico...</span>
    </div>
  );

  if (erro || dados.length === 0) return (
    <div style={page}>
      <h1 style={pageTitle}>Histórico de Sprints</h1>
      <div style={{ color:"#475569", fontSize:14, marginTop:40, textAlign:"center" }}>
        {erro || "Nenhum dado histórico disponível."}
      </div>
    </div>
  );

  /* ── Métricas resumidas ── */
  const avgPct   = Math.round(dados.reduce((s, d) => s + d.pct, 0) / dados.length);
  const melhor   = dados.reduce((m, d) => d.pct > m.pct ? d : m, dados[0]);
  const atual    = dados[dados.length - 1];
  const anterior = dados.length >= 2 ? dados[dados.length - 2] : null;
  const tendencia = anterior ? atual.pct - anterior.pct : null;

  /* ── Chart data ── */
  const labels = dados.map(d => d.label);

  const chartData = {
    labels,
    datasets: [
      {
        type: "bar",
        label: "Total",
        data: dados.map(d => d.total),
        backgroundColor: "rgba(96,165,250,0.15)",
        borderColor: "rgba(96,165,250,0.35)",
        borderWidth: 1,
        borderRadius: 5,
        yAxisID: "y",
        datalabels: {
          anchor: "end",
          align: "end",
          color: "rgba(96,165,250,0.7)",
          font: { size: 11, weight: 700 },
          formatter: v => v,
        },
      },
      {
        type: "bar",
        label: "Fechados",
        data: dados.map(d => d.fechado),
        backgroundColor: "rgba(74,222,128,0.25)",
        borderColor: "rgba(74,222,128,0.5)",
        borderWidth: 1,
        borderRadius: 5,
        yAxisID: "y",
        datalabels: {
          anchor: "center",
          align: "center",
          color: "rgba(74,222,128,0.9)",
          font: { size: 11, weight: 700 },
          formatter: v => v,
        },
      },
      {
        type: "line",
        label: "Conclusão %",
        data: dados.map(d => d.pct),
        borderColor: "#22d3ee",
        backgroundColor: "rgba(34,211,238,0.06)",
        borderWidth: 2.5,
        pointBackgroundColor: dados.map(d => devColor(d.pct)),
        pointBorderColor: "#060d1e",
        pointBorderWidth: 1.5,
        pointRadius: 6,
        pointHoverRadius: 8,
        fill: false,
        tension: 0.3,
        yAxisID: "y2",
        datalabels: { display: false },
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { intersect: false, mode: "index" },
    layout: { padding: { top: 20 } },
    plugins: {
      legend: { labels: { color: "#94a3b8", font: { size: 11 }, padding: 16 } },
      tooltip: {
        backgroundColor: "rgba(8,18,42,0.95)",
        titleColor: "#e2e8f0",
        bodyColor: "#94a3b8",
        borderColor: "rgba(96,165,250,0.2)",
        borderWidth: 1,
        padding: 10,
        callbacks: {
          label: ctx => ctx.dataset.label === "Conclusão %"
            ? ` Conclusão: ${ctx.raw}%`
            : ` ${ctx.dataset.label}: ${ctx.raw}`,
        },
      },
    },
    scales: {
      x: {
        ticks: { color: "#475569", font: { size: 11 } },
        grid: { color: "rgba(96,165,250,0.06)" },
      },
      y: {
        ticks: { color: "#475569" },
        grid: { color: "rgba(96,165,250,0.06)" },
        title: { display: true, text: "Tickets", color: "#475569", font: { size: 10 } },
      },
      y2: {
        position: "right",
        min: 0, max: 100,
        ticks: { color: "#22d3ee", callback: v => v + "%" },
        grid: { drawOnChartArea: false },
        title: { display: true, text: "Conclusão %", color: "#22d3ee", font: { size: 10 } },
      },
    },
  };

  return (
    <div style={page}>

      {/* ── Header ── */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 style={pageTitle}>Histórico de Sprints</h1>
          <div style={pageSub}>Evolução das últimas {dados.length} sprints</div>
        </div>
        <div style={{ display: "flex", gap: 6, alignSelf: "center" }}>
          {["todas", "programadas"].map(op => (
            <button
              key={op}
              onClick={() => setFiltro(op)}
              style={{
                padding: "6px 14px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer",
                border: `1px solid ${filtro === op ? "rgba(96,165,250,0.4)" : "rgba(96,165,250,0.2)"}`,
                background: filtro === op ? "rgba(96,165,250,0.12)" : "transparent",
                color: filtro === op ? "#60a5fa" : "#475569",
                transition: "all 0.15s",
              }}
            >
              {op === "todas" ? "Todas" : "Programadas"}
            </button>
          ))}
        </div>
      </div>

      {/* ── KPI cards ── */}
      <div style={kpiGrid}>
        <KpiCard label="Sprints analisadas" value={dados.length} cor="#60a5fa" />
        <KpiCard label="Média de conclusão" value={`${avgPct}%`} cor={devColor(avgPct)} />
        <KpiCard label="Melhor sprint" value={`${melhor.label} · ${melhor.pct}%`} cor="#4ade80" small />
        {tendencia !== null && (
          <KpiCard
            label="Tendência (vs anterior)"
            value={`${tendencia >= 0 ? "+" : ""}${tendencia}%`}
            cor={tendencia >= 0 ? "#4ade80" : "#f87171"}
          />
        )}
      </div>

      {/* ── Gráfico principal ── */}
      <div style={card}>
        <div style={cardTitle}>Evolução por Sprint</div>
        <div style={{ height: 300 }}>
          <Chart type="bar" data={chartData} options={chartOptions} />
        </div>
      </div>

      {/* ── Comparativo atual vs anterior ── */}
      {anterior && (
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
          <CompCard label="Sprint anterior" dados={anterior} />
          <CompCard label="Sprint atual" dados={atual} destaque />
        </div>
      )}

      {/* ── Heatmap de produtividade ── */}
      <div style={card}>
        <div style={cardTitle}>Heatmap de Entrega por Sprint</div>
        <Heatmap dados={dados} />
      </div>

      {/* ── Tabela de histórico ── */}
      <div style={card}>
        <div style={cardTitle}>Detalhes por Sprint</div>
        <table style={{ width:"100%", borderCollapse:"collapse" }}>
          <thead>
            <tr>
              {["Sprint","Total","Fechados","Abertos","Conclusão"].map(h => (
                <th key={h} style={th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[...dados].reverse().map((d, i) => ( // tabela: mais recente primeiro
              <tr key={i} style={{ background: i % 2 === 0 ? "rgba(96,165,250,0.03)" : "transparent" }}>
                <td style={td}><span style={{ fontWeight: 700, color: "#e2e8f0" }}>{d.label}</span></td>
                <td style={{ ...td, textAlign:"center" }}>{d.total}</td>
                <td style={{ ...td, textAlign:"center", color:"#4ade80", fontWeight:700 }}>{d.fechado}</td>
                <td style={{ ...td, textAlign:"center", color:"#fbbf24", fontWeight:700 }}>{d.aberto}</td>
                <td style={{ ...td, textAlign:"center" }}>
                  <span style={{
                    display:"inline-block", padding:"2px 10px", borderRadius:6,
                    fontSize:11, fontWeight:800,
                    color: devColor(d.pct),
                    background: `${devColor(d.pct)}18`,
                    border: `1px solid ${devColor(d.pct)}44`,
                  }}>{d.pct}%</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

    </div>
  );
}

/* ─── Heatmap ────────────────────────────────────────────────────── */

function heatColor(pct) {
  if (pct === null) return "rgba(96,165,250,0.06)";
  if (pct >= 90) return "#22d3ee";
  if (pct >= 70) return "#4ade80";
  if (pct >= 50) return "#fbbf24";
  if (pct >= 30) return "#fb923c";
  return "#f87171";
}

function Heatmap({ dados }) {
  const [hover, setHover] = useState(null);

  // Expande para mostrar até 26 semanas (sprints), preenchendo vazios
  const MAX = 26;
  const blocks = Array.from({ length: MAX }, (_, i) => {
    const d = dados[dados.length - MAX + i];
    return d || null;
  });

  // Agrupa em linhas de 13 (2 linhas de semanas)
  const rows = [blocks.slice(0, 13), blocks.slice(13)];

  return (
    <div>
      {/* Legenda */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
        <span style={{ fontSize: 10, color: "#475569" }}>Conclusão:</span>
        {[["< 30%", "#f87171"], ["30–50%", "#fb923c"], ["50–70%", "#fbbf24"], ["70–90%", "#4ade80"], ["≥ 90%", "#22d3ee"]].map(([l, c]) => (
          <div key={l} style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <div style={{ width: 12, height: 12, borderRadius: 3, background: c }} />
            <span style={{ fontSize: 10, color: "#475569" }}>{l}</span>
          </div>
        ))}
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <div style={{ width: 12, height: 12, borderRadius: 3, background: "rgba(96,165,250,0.06)", border: "1px solid rgba(96,165,250,0.1)" }} />
          <span style={{ fontSize: 10, color: "#475569" }}>Sem dados</span>
        </div>
      </div>

      {/* Grid */}
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {rows.map((row, ri) => (
          <div key={ri} style={{ display: "flex", gap: 6 }}>
            {row.map((d, ci) => {
              const idx = ri * 13 + ci;
              const isHover = hover === idx;
              return (
                <div
                  key={ci}
                  onMouseEnter={() => setHover(idx)}
                  onMouseLeave={() => setHover(null)}
                  style={{
                    flex: 1, height: 44, borderRadius: 6,
                    background: d ? heatColor(d.pct) : "rgba(96,165,250,0.06)",
                    border: `1px solid ${d ? `${heatColor(d.pct)}60` : "rgba(96,165,250,0.1)"}`,
                    opacity: d ? 1 : 0.4,
                    cursor: d ? "default" : "default",
                    display: "flex", flexDirection: "column",
                    alignItems: "center", justifyContent: "center",
                    transition: "transform 0.1s, box-shadow 0.1s",
                    transform: isHover && d ? "translateY(-2px)" : "none",
                    boxShadow: isHover && d ? `0 4px 12px ${heatColor(d.pct)}40` : "none",
                    position: "relative",
                  }}
                >
                  {d ? (
                    <>
                      <span style={{ fontSize: 11, fontWeight: 800, color: "#060d1e", lineHeight: 1 }}>{d.pct}%</span>
                      <span style={{ fontSize: 9, color: "rgba(6,13,30,0.7)", marginTop: 2 }}>{d.label}</span>
                    </>
                  ) : (
                    <span style={{ fontSize: 9, color: "rgba(96,165,250,0.2)" }}>—</span>
                  )}
                  {/* Tooltip */}
                  {isHover && d && (
                    <div style={{
                      position: "absolute", bottom: "calc(100% + 6px)", left: "50%",
                      transform: "translateX(-50%)", zIndex: 10,
                      background: "rgba(8,18,42,0.97)", border: "1px solid rgba(96,165,250,0.2)",
                      borderRadius: 8, padding: "6px 10px", whiteSpace: "nowrap",
                      fontSize: 11, color: "#e2e8f0", pointerEvents: "none",
                    }}>
                      <div style={{ fontWeight: 700 }}>Sprint {d.label}</div>
                      <div style={{ color: "#94a3b8", marginTop: 2 }}>
                        {d.fechado}/{d.total} entregues · {d.pct}%
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Eixo de labels */}
      <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
        {rows[1].map((d, ci) => (
          <div key={ci} style={{ flex: 1, textAlign: "center", fontSize: 9, color: "#334155" }}>
            {d?.label || ""}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Sub-componentes ────────────────────────────────────────────── */

function KpiCard({ label, value, cor, small }) {
  return (
    <div style={kpiCard}>
      <div style={{ fontSize:10, fontWeight:700, color:"#475569", textTransform:"uppercase", letterSpacing:0.8 }}>{label}</div>
      <div style={{ fontSize: small ? 16 : 28, fontWeight:800, color: cor, lineHeight:1, marginTop:6 }}>{value}</div>
    </div>
  );
}

function CompCard({ label, dados, destaque }) {
  const c = devColor(dados.pct);
  return (
    <div style={{
      ...card,
      border: destaque ? `1px solid ${c}44` : "1px solid rgba(96,165,250,0.1)",
      background: destaque ? `${c}08` : "rgba(11,22,42,0.5)",
    }}>
      <div style={{ fontSize:11, fontWeight:700, color: destaque ? c : "#475569", textTransform:"uppercase", letterSpacing:0.8, marginBottom:14 }}>
        {label} — Sprint {dados.label}
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:12 }}>
        {[
          { l:"Total",    v: dados.total,   cor:"#60a5fa" },
          { l:"Fechados", v: dados.fechado, cor:"#4ade80" },
          { l:"Abertos",  v: dados.aberto,  cor:"#fbbf24" },
        ].map(({ l, v, cor: cc }) => (
          <div key={l} style={{ textAlign:"center" }}>
            <div style={{ fontSize:22, fontWeight:800, color:cc }}>{v}</div>
            <div style={{ fontSize:10, color:"#475569", marginTop:2 }}>{l}</div>
          </div>
        ))}
      </div>
      <div style={{ marginTop:14, textAlign:"center" }}>
        <span style={{ fontSize:24, fontWeight:800, color:c }}>{dados.pct}%</span>
        <div style={{ fontSize:10, color:"#475569", marginTop:2 }}>Conclusão</div>
        <div style={{ height:4, background:"rgba(255,255,255,0.06)", borderRadius:3, marginTop:8, overflow:"hidden" }}>
          <div style={{ height:"100%", width:`${dados.pct}%`, background:c, borderRadius:3, transition:"width 0.6s" }} />
        </div>
      </div>
    </div>
  );
}

/* ─── Estilos ─────────────────────────────────────────────────────── */

const page    = { padding:"28px 32px", display:"flex", flexDirection:"column", gap:20 };
const pageTitle = { color:"#f1f5f9", fontSize:22, fontWeight:700, margin:0 };
const pageSub   = { color:"#475569", fontSize:13, marginTop:4 };
const kpiGrid   = { display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:14 };
const kpiCard   = {
  background:"rgba(11,22,42,0.6)", border:"1px solid rgba(96,165,250,0.1)",
  borderRadius:12, padding:"16px 18px", display:"flex", flexDirection:"column", gap:4,
};
const card = {
  background:"rgba(11,22,42,0.6)", border:"1px solid rgba(96,165,250,0.1)",
  borderRadius:14, padding:"20px 22px",
};
const cardTitle = { fontSize:11, fontWeight:700, color:"#cbd5e1", textTransform:"uppercase", letterSpacing:1.2, marginBottom:16 };
const th = { padding:"8px 12px", fontSize:10, fontWeight:700, color:"#475569", textTransform:"uppercase", letterSpacing:0.5, textAlign:"left", borderBottom:"1px solid rgba(96,165,250,0.1)" };
const td = { padding:"10px 12px", fontSize:12, color:"#94a3b8", borderBottom:"1px solid rgba(96,165,250,0.05)" };
